/**
 * Base64 detection and decoding utilities.
 */

declare const atob: (str: string) => string;

/**
 * Checks whether a string is Base64 encoded.
 * Strips non-base64 characters and validates that it decodes to a valid UTF-8 string
 * without excessive control characters (garbage).
 */
export const isBase64 = (str: string | null | undefined): boolean => {
  if (!str || typeof str !== 'string') return false;

  const cleaned = str.replace(/[^A-Za-z0-9+/=]/g, '');
  if (cleaned.length < 4) return false;

  try {
    const binary = atob(cleaned);
    const decoded = decodeURIComponent(escape(binary));

    // Check for control/garbage characters
    let garbageCount = 0;
    for (let i = 0; i < decoded.length; i++) {
      const code = decoded.charCodeAt(i);
      if (code < 32 && code !== 9 && code !== 10 && code !== 13) {
        garbageCount++;
      }
    }

    // If more than 10% of characters are garbage/control characters, it's not base64
    if (garbageCount / decoded.length > 0.1) {
      return false;
    }

    // If the decoded string is identical to the input or cleaned input, it's not base64
    if (decoded === str || decoded === cleaned) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

/**
 * Decodes a Base64 string to a UTF-8 string.
 * Automatically cleans any trailing/non-base64 characters before decoding.
 * If decoding fails, it returns the original string.
 */
export const decodeBase64 = (str: string | null | undefined): string => {
  if (!str) return '';
  if (typeof str !== 'string') return String(str);

  try {
    const cleaned = str.replace(/[^A-Za-z0-9+/=]/g, '');
    const binary = atob(cleaned);
    return decodeURIComponent(escape(binary));
  } catch (error) {
    console.error('Failed to decode base64 string:', error);
    return str;
  }
};

/**
 * Detects if a string is Base64 encoded, and if so, decodes it.
 * Otherwise, returns the original string as-is.
 */
export const detectAndDecode = (str: string | null | undefined): string => {
  if (!str) return '';
  if (isBase64(str)) {
    return decodeBase64(str);
  }
  return str;
};
